// src/components/tutorials/JS_Data_Types.js
import React, { useRef } from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Data_Types() {
  const refs = {
    primitive: useRef(null),
    nonPrimitive: useRef(null),
    typeof: useRef(null),
    notes: useRef(null),
  };

  const scrollTo = (key) => {
    refs[key]?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const topics = [
    { key: "primitive", label: "Primitive Data Types" },
    { key: "nonPrimitive", label: "Non-Primitive Data Types" },
    { key: "typeof", label: "typeof Operator" },
    { key: "notes", label: "Special Notes" },
  ];

  const primitiveTypes = [
    {
      op: "String",
      name: "Text values",
      desc: "Represents text enclosed in quotes.",
      example: `"Hello", 'World'`,
    },
    {
      op: "Number",
      name: "Numeric values",
      desc: "Represents integers and floating-point numbers.",
      example: "10, 3.14",
    },
    {
      op: "Boolean",
      name: "Logical values",
      desc: "Can only be true or false.",
      example: "true, false",
    },
    {
      op: "Undefined",
      name: "No value assigned",
      desc: "Declared but not assigned a value.",
      example: "let x;",
    },
    {
      op: "Null",
      name: "Empty value",
      desc: "Represents intentional absence of value.",
      example: "let y = null;",
    },
    {
      op: "Symbol",
      name: "Unique identifier",
      desc: "Used to create unique object keys (ES6).",
      example: "Symbol('id')",
    },
    {
      op: "BigInt",
      name: "Large integers",
      desc: "Handles numbers larger than 2^53-1 (ES11).",
      example: "123456789012345678901n",
    },
  ];

  const nonPrimitiveTypes = [
    {
      op: "Object",
      name: "Key-value collection",
      desc: "Stores properties and their values.",
      example: `{ name: "John", age: 30 }`,
    },
    {
      op: "Array",
      name: "Ordered list",
      desc: "Special object type for ordered data.",
      example: "[1, 2, 3]",
    },
    {
      op: "Function",
      name: "Reusable code block",
      desc: "Encapsulates behavior or logic.",
      example: "function greet() { return 'Hi'; }",
    },
  ];

  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">JavaScript Data Types</h1>
      <p className="tutorial-intro">
        JavaScript variables can hold different kinds of data. The type
        determines what kind of operations can be performed on it. Let's explore
        all data types with examples.
      </p>

      {/* 🔹 Key Points Navigation */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
        }}
      >
        <h1 style={{ color: "#075985", fontSize: "2.3rem", margin: 0 }}>
          Data Type Categories
        </h1>
        <p style={{ fontSize: "1.05rem", marginTop: "0.5rem" }}>
          JavaScript has two main categories: <b>Primitive</b> and{" "}
          <b>Non-Primitive (Reference)</b>.
        </p>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            marginTop: "1rem",
          }}
        >
          {topics.map((t) => (
            <p key={t.key} style={{ margin: "0.25rem 0" }}>
              <button
                onClick={() => scrollTo(t.key)}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "#000",
                  textDecoration: "underline",
                  cursor: "pointer",
                  fontSize: "1.05rem",
                }}
              >
                {t.label}
              </button>
            </p>
          ))}
        </div>
      </div>

      {/* 1️⃣ Primitive Data Types */}
      <div ref={refs.primitive} className="tutorial-section">
        <h2 className="section-title">1️⃣ Primitive Data Types</h2>
        <p>
          Primitive data types hold single values and are immutable (they cannot
          be changed directly).
        </p>

        <div
          className="reference-table-container"
          style={{
            marginTop: "1rem",
            overflowX: "auto",
            borderRadius: "10px",
            boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          }}
        >
          <table
            className="reference-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "1rem",
              backgroundColor: "#fff",
            }}
          >
            <thead style={{ background: "#0ea5e9", color: "white" }}>
              <tr>
                <th style={{ textAlign: "left", padding: "0.8rem 1rem" }}>
                  Type
                </th>
                <th style={{ textAlign: "left", padding: "0.8rem 1rem" }}>
                  Name
                </th>
                <th style={{ textAlign: "left", padding: "0.8rem 1rem" }}>
                  Description
                </th>
                <th style={{ textAlign: "left", padding: "0.8rem 1rem" }}>
                  Example
                </th>
              </tr>
            </thead>
            <tbody>
              {primitiveTypes.map((item, index) => (
                <tr
                  key={index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#f9fafb" : "#fff",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <td
                    style={{
                      padding: "0.7rem 1rem",
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "#2563eb",
                    }}
                  >
                    {item.op}
                  </td>
                  <td style={{ padding: "0.7rem 1rem", fontWeight: 500 }}>
                    {item.name}
                  </td>
                  <td style={{ padding: "0.7rem 1rem" }}>{item.desc}</td>
                  <td
                    style={{
                      padding: "0.7rem 1rem",
                      fontFamily: "monospace",
                      color: "#0d9488",
                      fontWeight: "bold",
                    }}
                  >
                    {item.example}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let name = "Alice";
let age = 25;
let isStudent = true;
let x;
let y = null;
let id = Symbol("id");
let big = 123456789012345678901n;

console.log(typeof name);
console.log(typeof big);`}
          />
          <TryItButton
            code={`let name = "Alice";
let age = 25;
let isStudent = true;
let x;
let y = null;
let id = Symbol("id");
let big = 123456789012345678901n;

console.log(typeof name);
console.log(typeof big);`}
          />
        </div>
        <OutputBox>
          <pre>string{"\n"}bigint</pre>
        </OutputBox>
      </div>

      {/* 2️⃣ Non-Primitive (Reference) Data Types */}
      <div ref={refs.nonPrimitive} className="tutorial-section">
        <h2 className="section-title">
          2️⃣ Non-Primitive (Reference) Data Types
        </h2>
        <p>
          Non-primitive types hold collections or complex structures and are
          stored by reference.
        </p>

        <div
          className="reference-table-container"
          style={{
            marginTop: "1rem",
            overflowX: "auto",
            borderRadius: "10px",
            boxShadow: "0 0 10px rgba(0,0,0,0.1)",
          }}
        >
          <table
            className="reference-table"
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "1rem",
              backgroundColor: "#fff",
            }}
          >
            <thead style={{ background: "#0ea5e9", color: "white" }}>
              <tr>
                <th style={{ textAlign: "left", padding: "0.8rem 1rem" }}>
                  Type
                </th>
                <th style={{ textAlign: "left", padding: "0.8rem 1rem" }}>
                  Name
                </th>
                <th style={{ textAlign: "left", padding: "0.8rem 1rem" }}>
                  Description
                </th>
                <th style={{ textAlign: "left", padding: "0.8rem 1rem" }}>
                  Example
                </th>
              </tr>
            </thead>
            <tbody>
              {nonPrimitiveTypes.map((item, index) => (
                <tr
                  key={index}
                  style={{
                    backgroundColor: index % 2 === 0 ? "#f9fafb" : "#fff",
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <td
                    style={{
                      padding: "0.7rem 1rem",
                      fontFamily: "monospace",
                      fontWeight: "bold",
                      color: "#2563eb",
                    }}
                  >
                    {item.op}
                  </td>
                  <td style={{ padding: "0.7rem 1rem", fontWeight: 500 }}>
                    {item.name}
                  </td>
                  <td style={{ padding: "0.7rem 1rem" }}>{item.desc}</td>
                  <td
                    style={{
                      padding: "0.7rem 1rem",
                      fontFamily: "monospace",
                      color: "#0d9488",
                      fontWeight: "bold",
                    }}
                  >
                    {item.example}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`const person = { name: "John", age: 30 };
const numbers = [1, 2, 3];
function greet() { return "Hi"; }

console.log(typeof person);
console.log(typeof numbers);
console.log(typeof greet);`}
          />
          <TryItButton
            code={`const person = { name: "John", age: 30 };
const numbers = [1, 2, 3];
function greet() { return "Hi"; }

console.log(typeof person);
console.log(typeof numbers);
console.log(typeof greet);`}
          />
        </div>
        <OutputBox>
          <pre>
            object{"\n"}object{"\n"}function
          </pre>
        </OutputBox>
      </div>

      {/* 3️⃣ typeof Operator */}
      <div ref={refs.typeof} className="tutorial-section">
        <h2 className="section-title">3️⃣ typeof Operator</h2>
        <p>
          The <code>typeof</code> operator returns the type of a variable.
        </p>
        <div className="code-demo-box">
          <CodeCopyBlock
            code={`console.log(typeof "Hello");   
console.log(typeof 42);        
console.log(typeof true);      
console.log(typeof undefined); 
console.log(typeof null);      
console.log(typeof Symbol());  
console.log(typeof 123n);      
console.log(typeof [1,2,3]);   
console.log(typeof {a:1});     
console.log(typeof function(){});`}
          />
          <TryItButton
            code={`console.log(typeof "Hello");
console.log(typeof 42);
console.log(typeof true);
console.log(typeof undefined);
console.log(typeof null);
console.log(typeof Symbol());
console.log(typeof 123n);
console.log(typeof [1,2,3]);
console.log(typeof {a:1});
console.log(typeof function(){});`}
          />
        </div>
        <OutputBox>
          <pre>
            string{"\n"}number{"\n"}boolean{"\n"}undefined{"\n"}object{"\n"}
            symbol{"\n"}bigint{"\n"}object{"\n"}object{"\n"}function
          </pre>
        </OutputBox>
      </div>

      {/* Next Steps */}
      <div
        className="next-steps-container"
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Learn about <b>JavaScript Variables</b> to store and use data.
          </li>
          <li>
            Understand <b>Data Types</b> like strings, numbers, and booleans.
          </li>
          <li>
            Explore <b>Operators</b> to perform calculations and comparisons.
          </li>
        </ul>
      </div>
    </div>
  );
}
